'use client';

import {
  ApiResponse,
  CommonCourseUtils,
  CourseViewModel,
  CurrencyInfo,
  groupBy,
  PromoViewModel,
} from '@hidayahlab/academy-suite-corelib';
import {
  AssignmentsIcon,
  Award05Icon,
  Calendar03Icon,
  FileUnknownIcon,
  Mic01Icon,
  Mortarboard02Icon,
  PlayCircleIcon,
  Time04Icon,
  UserMultiple02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FacebookIcon, TicketIcon, XCircleIcon } from '@tajdid/tajdid-ui/icons';
import { User } from 'lucia';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import CoursePricing from '@/components/course-card/course-pricing';
import { EnrollmentMessage } from '@/components/enrollment-message';
import { PromoInputForm } from '@/components/promo';
import SectionTitle from '@/components/section-title/section-title';
import SupportNumber from '@/components/support-number';
import { BatchWithClassRoutine } from '@/drizzle/models';
import {
  useFilteredCurriculumByBatchVisibility,
  useGetBatchCurriculumVisibility,
} from '@/hooks/use-batch-curriculum-visibility';
import { useFacebookPixelEvents } from '@/hooks/use-facebook-pixel-events';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { Button } from '@/ui';
import NumberFormatter from '@/utils/bn';
import { dateUtils } from '@/utils/date-utils';
import { checkGenderRestriction } from '@/utils/gender-utils';
import { useSearchParams } from 'next/navigation';
import { getPromoByCode } from '../../../actions/promo-action';
import GenderRestrictionMessage from '../gender-restriction-message';
import BatchStartCountdown from './batch-start-countdown';

export type CourseCardProps = {
  isEnrolled: boolean;
  courseDetails: CourseViewModel;
  promoRes?: ApiResponse<PromoViewModel>;
  batch: BatchWithClassRoutine;
  user?: User | null;
  currencyInfo: CurrencyInfo;
  enrolledBatchNumber?: number | null;
};

export default function LiveCourseMetaAction({
  isEnrolled,
  courseDetails,
  batch,
  promoRes,
  user,
  currencyInfo,
  enrolledBatchNumber,
}: CourseCardProps) {
  const locale = useLocale();
  const router = useRouter();
  const t = useTranslations();
  const tPayment = useTranslations('payment-page');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isPromoApplied = !!searchParams.get('promo');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoInputValue, setPromoInputValue] = useState('');
  const [promo, setPromoData] = useState<PromoViewModel | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState('');
  const { trackAddToCart } = useFacebookPixelEvents();
  const {
    isFree,
    price,
    slug,
    curriculum,
    discount,
    discountType = 'PERCENTAGE',
  } = courseDetails;

  const { isGenderAllowed, showGenderMessage } = checkGenderRestriction(
    courseDetails.genderAccess,
    user?.gender,
  );

  const formattedBatchStartDate = dateUtils.formatter.forBatchStartDate(
    batch.startDate,
    locale,
  );

  const { classRoutineDaysName, formattedClassTime } =
    dateUtils.formatter.forBatchClassRoutineAndTime(
      batch.classRoutines,
      locale,
    );

  const { data: visibilityData } = useGetBatchCurriculumVisibility(batch.id);

  const filteredCurriculumContents = useFilteredCurriculumByBatchVisibility(
    curriculum ?? [],
    visibilityData,
  );

  const contents =
    filteredCurriculumContents?.flatMap(item => item.contents) ?? [];
  const contentGroupByType = groupBy(contents, 'type');
  let discountPrice = CommonCourseUtils.calculateDiscountedPrice(
    price,
    discount,
    discountType,
  );

  useEffect(() => {
    if (promoRes?.status === 'success') {
      setPromoData(promoRes.data);
    }
  }, [promoRes]);

  if (isPromoApplied && promo) {
    discountPrice = CommonCourseUtils.calculateDiscountedPrice(
      price,
      promo.value,
      promo.type,
    );
  }

  const handleBuyNowButtonClick = (
    isEnrolled: boolean,
    isPromoApplied: boolean,
    courseDetails: CourseViewModel,
    slug: string,
    promo?: PromoViewModel,
  ) => {
    let path = '';

    if (isEnrolled) {
      path = `/my-classes/live/${courseDetails.id}`;
    } else if (promo && isPromoApplied) {
      path = `/payment/${courseDetails.id}/${slug}?promo=${promo.code}`;
    } else {
      trackAddToCart(
        {
          email: user?.email,
          phone: user?.phone,
        },
        {
          value: courseDetails.price,
        },
      );

      path = `/payment/${courseDetails.id}/${slug}`;
    }

    router.push(path);
  };

  const handleApplyPromo = async () => {
    setErrorMessage('');
    const res = await getPromoByCode({
      code: promoInputValue,
      courseId: courseDetails.id,
    });

    if (res.status === 'success') {
      setPromoData(res.data);
      router.push(`${pathname}?promo=${res.data?.code}#course_card`);
      setShowPromoInput(false);
    } else {
      setErrorMessage('The promo is not valid');
    }
  };

  const upcomingBatch = batch.status === 'UPCOMING';

  return (
    <div className="top-24 rounded-md md:shadow-sm h-fit md:mt-0 md:sticky">
      <div className="grid grid-cols-2 gap-4 p-2 md:p-4">
        <div className="flex flex-col gap-x-2 items-center px-6 py-3 bg-gray-50 rounded-sm md:flex-row max-md:text-center">
          <div className="p-2">
            <HugeiconsIcon
              icon={Time04Icon}
              width={20}
              height={20}
              className="text-primary-600 size-5"
            />
          </div>
          <div>
            <p className="text-sm font-normal text-body">
              {t('course-details.courseBenefitsCard.class-routine')}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {classRoutineDaysName?.join(', ')}
              <span className="text-sm font-semibold text-gray-700">
                {' '}
                | {formattedClassTime}
              </span>
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-x-2 items-center px-6 py-3 bg-gray-50 rounded-sm md:flex-row max-md:text-center">
          <div className="p-2">
            <HugeiconsIcon
              icon={UserMultiple02Icon}
              className="text-primary-600 size-5"
            />
          </div>
          <div>
            <p className="text-sm font-normal text-body">
              {t('course-details.courseBenefitsCard.total-seats')}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {locale === 'bn'
                ? NumberFormatter(batch.seats ?? 0)
                : batch.seats}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-x-2 items-center px-6 py-3 bg-gray-50 rounded-sm md:flex-row max-md:text-center">
          <div className="p-2">
            <HugeiconsIcon
              icon={Mortarboard02Icon}
              className="text-primary-600 size-5"
            />
          </div>
          <div>
            <p className="text-sm font-normal text-body">
              {t('course-details.courseBenefitsCard.admission-time-left')}
            </p>
            <BatchStartCountdown batchStartDate={batch.startDate} />
          </div>
        </div>
        <div className="flex flex-col gap-x-2 items-center px-6 py-3 bg-gray-50 rounded-sm md:flex-row max-md:text-center">
          <div className="p-2">
            <HugeiconsIcon
              icon={Calendar03Icon}
              className="text-primary-600 size-5"
            />
          </div>
          <div>
            <p className="text-sm font-normal text-body">
              {t('course-details.courseBenefitsCard.batch-start')}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {formattedBatchStartDate}
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 pb-8">
        <SectionTitle size="md">
          {t('course-details.courseBenefitsCard.card-title')}
        </SectionTitle>

        <div className="pt-2 md:pt-4">
          <div className="flex gap-3 justify-around items-center lg:gap-4">
            <div className="flex flex-col md:flex-row max-md:text-center items-center w-[50%] gap-2 ">
              <div className="p-2 rounded-full">
                <HugeiconsIcon
                  icon={Award05Icon}
                  className="text-primary-600 size-5"
                />
              </div>

              {batch.name ? (
                <div>
                  <p className="text-sm text-gray-600">
                    {t('course-card-live.batch')}
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {batch.name}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600">
                    {t('course-details.courseBenefitsCard.batch-no')}
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {locale === 'bn'
                      ? NumberFormatter(batch.number ?? 0)
                      : batch.number}
                  </p>
                </div>
              )}
            </div>
            <div className=" w-[1px] h-[60px] rounded-[32px] border-background"></div>
            <div className="flex flex-col md:flex-row max-md:text-center 2xl:pl-3 w-[50%] items-center gap-2 ">
              <div className="p-2 rounded-full">
                <HugeiconsIcon
                  icon={PlayCircleIcon}
                  className="text-primary-600 size-5"
                />
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t('course-details.courseBenefitsCard.live-class')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(contentGroupByType.LESSON?.length ?? 0)
                    : (contentGroupByType.LESSON?.length ?? 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="py-3">
            <div className="w-full h-[1px] border-background-bottom bg-[#e5e5e5]"></div>
          </div>
          <div className="flex gap-3 justify-around items-center lg:gap-4">
            <div className="flex flex-col md:flex-row max-md:text-center items-center w-[50%] gap-2">
              <div className="p-2 rounded-full">
                <HugeiconsIcon
                  icon={FileUnknownIcon}
                  className="text-primary-600 size-5"
                />
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t('course-details.courseBenefitsCard.number-of-quizzes')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(contentGroupByType.QUIZ?.length ?? 0)
                    : (contentGroupByType.QUIZ?.length ?? 0)}
                </p>
              </div>
            </div>
            <div className="w-[1px] h-[60px] rounded-[32px] border-background"></div>
            <div className="flex flex-col md:flex-row max-md:text-center 2xl:pl-3 w-[50%] items-center gap-2 ">
              <div className="p-2 rounded-full">
                <HugeiconsIcon
                  icon={Mic01Icon}
                  className="text-primary-600 size-5"
                />
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t(
                    'course-details.courseBenefitsCard.number-of-voice-quizzes',
                  )}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(
                        contentGroupByType.VOICE_QUIZ?.length ?? 0,
                      )
                    : (contentGroupByType.VOICE_QUIZ?.length ?? 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="py-3">
            <div className="w-full h-[1px] border-background-bottom bg-[#e5e5e5]"></div>
          </div>

          <div className="flex gap-3 justify-around items-center lg:gap-4">
            <div className="flex flex-col md:flex-row max-md:text-center items-center w-[50%] gap-2">
              <div className="p-2 rounded-full">
                <HugeiconsIcon
                  icon={AssignmentsIcon}
                  className="text-primary-600 size-5"
                />
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t('course-details.courseBenefitsCard.number-of-assignments')}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {locale === 'bn'
                    ? NumberFormatter(
                        contentGroupByType.ASSIGNMENT?.length ?? 0,
                      )
                    : (contentGroupByType.ASSIGNMENT?.length ?? 0)}
                </p>
              </div>
            </div>
            <div className="w-[1px] h-[60px] rounded-[32px] border-background"></div>
            <div className="flex flex-col md:flex-row max-md:text-center 2xl:pl-3 w-[50%] items-center gap-2 ">
              <div className="p-2 rounded-full">
                <FacebookIcon className="text-primary-600 size-5" />
              </div>

              <div>
                <p className="text-sm text-gray-600">
                  {t('course-details.courseBenefitsCard.support-group')}
                </p>
                <p className="text-lg font-semibold text-gray-900">Facebook</p>
              </div>
            </div>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 z-50 px-4 py-1 pb-4 w-full bg-gray-50 md:static md:bg-transparent md:py-1">
          <div className="hidden py-0 md:py-4 md:flex">
            <div className="w-full border-b border-gray-200"></div>
          </div>
          {!isEnrolled && (
            <div className="flex justify-between items-center lg:mb-5">
              <CoursePricing
                price={price}
                discountPrice={discountPrice}
                discount={discount}
                isFree={isFree}
                currencyInfo={currencyInfo}
              />
              {!isPromoApplied && discount !== 100 && (
                <Button
                  variant="link"
                  className="flex items-center text-sm font-semibold cursor-pointer"
                  icon={<TicketIcon />}
                  color="gray"
                  iconPosition="start"
                  onClick={() => setShowPromoInput(true)}
                >
                  {tPayment('apply-promo-code')}
                </Button>
              )}
            </div>
          )}

          {isPromoApplied && promo && !errorMessage && !isEnrolled ? (
            <div className="flex justify-between items-center bg-gray-50 rounded-sm">
              <div className="flex gap-1 items-center">
                <TicketIcon className="text-primary-700" />
                <p className="text-base font-medium">{promo?.code}</p>
                <p>প্রোমো এপ্লাইড</p>
              </div>
              <Button
                onClick={() => {
                  router.push(`${pathname}#course_card`);
                  setPromoInputValue('');
                }}
                variant="icon"
                color="gray"
                icon={<XCircleIcon className="w-5 h-5" />}
              />
            </div>
          ) : (
            showPromoInput && (
              <div className="flex flex-col gap-2 mt-4">
                <div className="grid grid-cols-5 gap-2 items-center lg:grid-cols-7">
                  <div className="col-span-4 lg:col-span-6">
                    <PromoInputForm
                      label="প্রোমো কোড"
                      placeholder="আপনার প্রোমো কোডটি লিখুন"
                      onChange={e => setPromoInputValue(e.target.value)}
                      handleApplyPromo={handleApplyPromo}
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      onClick={() => {
                        setShowPromoInput(false);
                        setErrorMessage('');
                      }}
                      className="px-2 mt-5 h-10 lg:w-full min-w-12 lg:h-12"
                      variant="outlined"
                      color="gray"
                    >
                      <XCircleIcon />
                    </Button>
                  </div>
                </div>
                <div>
                  {errorMessage && (
                    <p className="text-error-500">{errorMessage}</p>
                  )}
                </div>
              </div>
            )
          )}

          <GenderRestrictionMessage
            showGenderMessage={showGenderMessage}
            isGenderAllowed={isGenderAllowed}
            genderAccess={courseDetails.genderAccess}
            locale={locale}
            className="mt-2"
          />

          {isEnrolled && enrolledBatchNumber && (
            <EnrollmentMessage
              batchNumber={enrolledBatchNumber}
              locale={locale}
            />
          )}

          <div className="mt-2">
            {!isGenderAllowed ? (
              isEnrolled ? (
                <Link href={`/my-classes/live/${courseDetails.id}`}>
                  <Button className="rounded-md bg-primary-500" fullWidth>
                    {enrolledBatchNumber
                      ? t('button.go-to-batch', {
                          batchNumber:
                            locale === 'bn'
                              ? NumberFormatter(enrolledBatchNumber)
                              : enrolledBatchNumber,
                        })
                      : t('button.continue')}
                  </Button>
                </Link>
              ) : (
                <Button
                  className="rounded-md bg-primary-500"
                  fullWidth
                  disabled={true}
                >
                  {t('button.buy-the-course')}
                </Button>
              )
            ) : (
              <>
                {courseDetails.format === 'LIVE' && !upcomingBatch ? (
                  isEnrolled ? (
                    <Link href={`/my-classes/live/${courseDetails.id}`}>
                      <Button className="rounded-md bg-primary-500" fullWidth>
                        {enrolledBatchNumber
                          ? t('button.go-to-batch', {
                              batchNumber:
                                locale === 'bn'
                                  ? NumberFormatter(enrolledBatchNumber)
                                  : enrolledBatchNumber,
                            })
                          : t('button.continue')}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className="rounded-md bg-primary-500"
                      fullWidth
                      onClick={() =>
                        handleBuyNowButtonClick(
                          isEnrolled,
                          isPromoApplied,
                          courseDetails,
                          slug,
                          promo,
                        )
                      }
                    >
                      {t('button.buy-the-course')}
                    </Button>
                  )
                ) : (
                  <Button
                    className="rounded-md bg-primary-500"
                    fullWidth
                    disabled={true}
                  >
                    {t('button.upcoming')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <SupportNumber className="mt-4 max-md:hidden" />
      </div>
    </div>
  );
}
